import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { post, get, patch, del } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './EventCalendar.css';

const localizer = momentLocalizer(moment);

const RECURRENCE_WEEKDAYS = [
  { token: 'mon', label: 'Mon', longLabel: 'Monday', jsDay: 1 },
  { token: 'tue', label: 'Tue', longLabel: 'Tuesday', jsDay: 2 },
  { token: 'wed', label: 'Wed', longLabel: 'Wednesday', jsDay: 3 },
  { token: 'thu', label: 'Thu', longLabel: 'Thursday', jsDay: 4 },
  { token: 'fri', label: 'Fri', longLabel: 'Friday', jsDay: 5 },
  { token: 'sat', label: 'Sat', longLabel: 'Saturday', jsDay: 6 },
  { token: 'sun', label: 'Sun', longLabel: 'Sunday', jsDay: 0 },
];
const MAX_RECURRING_OCCURRENCES = 184;
const MAX_RECURRING_MONTHS = 6;
const PUBLIC_EVENT_PLACEHOLDER = "Perform at Chula's Request";

const requestDisplayTitle = (request) => {
  if (!request) return '';
  if (
    request.request_type === 'public' &&
    (
      request.status === 'pending' ||
      String(request.event_name || '').toLowerCase() === PUBLIC_EVENT_PLACEHOLDER.toLowerCase()
    )
  ) {
    return PUBLIC_EVENT_PLACEHOLDER;
  }
  return request.event_name || PUBLIC_EVENT_PLACEHOLDER;
};

const defaultPublicEventTitle = (request) => {
  if (!request || request.request_type !== 'public') return '';
  const storedTitle = String(request.event_name || '').trim();
  if (storedTitle && storedTitle.toLowerCase() !== PUBLIC_EVENT_PLACEHOLDER.toLowerCase()) {
    return storedTitle;
  }
  const requesterName = String(request.requester_name || '').trim();
  if (requesterName) return requesterName;
  return `Live Performance – ${request.event_date}`;
};

const parseDateInput = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatClockTime = (value) => {
  if (typeof value !== 'string') return '';
  const match = value.match(/^([01]?\d|2[0-3]):([0-5]\d)/);
  if (!match) return '';
  const hour = parseInt(match[1], 10);
  const minute = match[2];
  const period = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${minute} ${period}`;
};

const addMonthsToDate = (startDate, months) => {
  const year = startDate.getFullYear();
  const month = startDate.getMonth() + months;
  const day = startDate.getDate();
  const lastDayOfTargetMonth = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDayOfTargetMonth));
};

const getDefaultRecurrenceEndDate = (startDateString) => {
  const start = parseDateInput(startDateString);
  return start ? formatDateInput(addMonthsToDate(start, MAX_RECURRING_MONTHS)) : '';
};

const getWeekdayTokenForDate = (dateString) => {
  const date = parseDateInput(dateString);
  if (!date) return null;
  return RECURRENCE_WEEKDAYS.find(day => day.jsDay === date.getDay())?.token || null;
};

const getRecurrencePreview = (formData) => {
  if (!formData.recurring_enabled) {
    return { count: 0, error: null, effectiveEndDate: '' };
  }

  const start = parseDateInput(formData.event_date);
  if (!start) {
    return { count: 0, error: 'Choose a valid start date.', effectiveEndDate: '' };
  }

  const days = formData.recurrence_days || [];
  if (days.length === 0) {
    return {
      count: 0,
      error: 'Select at least one weekday for the recurring series.',
      effectiveEndDate: getDefaultRecurrenceEndDate(formData.event_date),
    };
  }

  const effectiveEndDate = formData.recurrence_end_date || getDefaultRecurrenceEndDate(formData.event_date);
  const end = parseDateInput(effectiveEndDate);
  if (!end) {
    return { count: 0, error: 'Choose a valid repeat-until date.', effectiveEndDate };
  }

  const maxEnd = addMonthsToDate(start, MAX_RECURRING_MONTHS);
  const maxEndDate = formatDateInput(maxEnd);
  if (end < start) {
    return { count: 0, error: 'Repeat-until date must be on or after the start date.', effectiveEndDate };
  }
  if (end > maxEnd) {
    return {
      count: 0,
      error: `Recurring series may not exceed ${MAX_RECURRING_MONTHS} months (max ${maxEndDate}).`,
      effectiveEndDate,
    };
  }

  let count = 0;
  const selectedDays = new Set(days);
  const current = new Date(start);
  while (current <= end) {
    const token = RECURRENCE_WEEKDAYS.find(day => day.jsDay === current.getDay())?.token;
    if (token && selectedDays.has(token)) {
      count += 1;
      if (count > MAX_RECURRING_OCCURRENCES) {
        return {
          count,
          error: `Recurring series may not exceed ${MAX_RECURRING_OCCURRENCES} occurrences.`,
          effectiveEndDate,
        };
      }
    }
    current.setDate(current.getDate() + 1);
  }

  if (count === 0) {
    return { count: 0, error: 'No matching dates in range for the selected weekdays.', effectiveEndDate };
  }

  return { count, error: null, effectiveEndDate, maxEndDate };
};

const getDefaultEventFormData = (eventDate = '') => ({
  title: '',
  description: '',
  event_date: eventDate,
  event_time: '',
  image_url: '',
  color: '#f97316', // Default Chulas orange
  rsvp_enabled: false,
  rsvp_slots: 50,
  rsvp_waitlist_enabled: true,
  is_featured: false,
  recurring_enabled: false,
  recurrence_days: [],
  recurrence_end_date: '',
});

const buildLocalEventDate = (dateValue, timeValue) => {
  const time = timeValue ? String(timeValue) : '12:00:00';
  const normalizedTime = /^\d{1,2}:\d{2}$/.test(time) ? `${time}:00` : time;
  return new Date(`${dateValue}T${normalizedTime}`);
};

const transformEventForCalendar = (event) => {
  const start = buildLocalEventDate(event.event_date, event.event_time);
  const hasExplicitEnd = !!(event.event_end_date || event.event_end_time);
  let end = hasExplicitEnd
    ? buildLocalEventDate(
        event.event_end_date || event.event_date,
        event.event_end_time || event.event_time
      )
    : new Date(start);
  if (Number.isNaN(end.getTime()) || end < start) {
    end = new Date(start);
  }

  const titlePrefixes = [
    event.source === 'wix' ? 'Ⓦ ' : '',
    event.source === 'wix' && event.external_action_required === true ? '↗ ' : '',
    event.is_recurring_occurrence ? '🔁 ' : '',
    event.is_featured ? '⭐ ' : '',
  ].join('');

  return {
    id: event.event_id,
    title: `${titlePrefixes}${event.title}`,
    rawTitle: event.title,
    start,
    end,
    description: event.description,
    event_date: event.event_date,
    event_time: event.event_time,
    event_end_date: event.event_end_date,
    event_end_time: event.event_end_time,
    event_timezone: event.event_timezone,
    image_url: event.image_url,
    color: event.color || '#f97316',
    rsvp_enabled: event.source === 'wix' ? false : (event.rsvp_enabled || false),
    rsvp_slots: event.rsvp_slots || 0,
    rsvp_waitlist_enabled: event.rsvp_waitlist_enabled || false,
    is_featured: event.is_featured || false,
    source: event.source,
    wix_event_id: event.wix_event_id,
    wix_registration_type: event.wix_registration_type,
    wix_registration_status: event.wix_registration_status,
    external_action_required: event.external_action_required === true,
    external_url: event.external_url,
    location_name: event.location_name,
    location_address: event.location_address,
    is_recurring_occurrence: event.is_recurring_occurrence || false,
    recurrence_group_id: event.recurrence_group_id,
    recurrence_type: event.recurrence_type,
    recurrence_days: event.recurrence_days || [],
    recurrence_start_date: event.recurrence_start_date,
    recurrence_end_date: event.recurrence_end_date,
    recurrence_index: event.recurrence_index,
    allDay: !event.event_time,
  };
};

const EventCalendar = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('calendar'); // 'calendar' or 'requests'

  const [events, setEvents] = useState([]);
  const [roomBookings, setRoomBookings] = useState([]); // Approved private room bookings
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [syncingWix, setSyncingWix] = useState(false);
  const [wixSyncResult, setWixSyncResult] = useState(null);
  const { getAuthToken } = useAuth();

  // Event Requests state
  const [eventRequests, setEventRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestStatusFilter, setRequestStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [publicEventTitle, setPublicEventTitle] = useState('');
  const [processingRequest, setProcessingRequest] = useState(false);
  const deepLinkLoaded = useRef(false);

  // Calendar navigation state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Form state
  const [formData, setFormData] = useState(getDefaultEventFormData());
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // RSVP management state
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [rsvpList, setRsvpList] = useState([]);
  const [rsvpStats, setRsvpStats] = useState(null);
  const [loadingRsvps, setLoadingRsvps] = useState(false);

  // Fetch events on mount
  useEffect(() => {
    fetchEvents();
    fetchApprovedRoomBookings();
    // Also fetch pending requests for badge count
    fetchPendingRequestsCount();
  }, []);

  // Fetch event requests when tab switches to requests or filter changes
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchEventRequests();
    }
  }, [activeTab, requestStatusFilter]);

  // Admin emails link directly to one request. Fetch that record explicitly
  // instead of scanning whichever status filter happens to be selected.
  useEffect(() => {
    if (deepLinkLoaded.current) return;
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get('requestId');
    if (params.get('tab') !== 'requests' && !requestId) return;

    deepLinkLoaded.current = true;
    setActiveTab('requests');
    if (!requestId) return;

    let cancelled = false;
    const loadRequest = async () => {
      try {
        setRequestsLoading(true);
        const token = await getAuthToken();
        const operation = get({
          apiName: 'ChulasAdminAPI',
          path: `/v1/admin/event-requests/${encodeURIComponent(requestId)}`,
          options: { headers: { Authorization: token } },
        });
        const response = await operation.response;
        const data = await response.body.json();
        if (cancelled || !data.request) return;
        setSelectedRequest(data.request);
        setAdminNotes(data.request.admin_notes || '');
        setPublicEventTitle(defaultPublicEventTitle(data.request));
        setShowRequestModal(true);
      } catch (err) {
        if (!cancelled) setError('The linked event request could not be loaded.');
      } finally {
        if (!cancelled) setRequestsLoading(false);
      }
    };
    loadRequest();
    return () => { cancelled = true; };
  }, [getAuthToken]);

  // Helper to fetch pending count for badge
  const fetchPendingRequestsCount = async () => {
    try {
      const token = await getAuthToken();
      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/event-requests?status=pending',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });
      const response = await restOperation.response;
      const data = await response.body.json();
      // Only update if we're not already on the requests tab
      if (activeTab !== 'requests') {
        setEventRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching pending requests count:', err);
    }
  };

  // Fetch approved private room bookings for calendar display
  const fetchApprovedRoomBookings = async () => {
    try {
      const token = await getAuthToken();
      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/event-requests?status=approved',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });
      const response = await restOperation.response;
      const data = await response.body.json();

      // Filter for private room bookings and transform for calendar
      const privateBookings = (data.requests || [])
        .filter(req => req.request_type === 'private')
        .map(booking => {
          // Determine color based on room
          let color = '#9333ea'; // Purple default for private rooms
          if (booking.room_preference === 'jalisco') {
            color = '#7c3aed'; // Violet for Jalisco
          } else if (booking.room_preference === 'zacatecas') {
            color = '#a855f7'; // Light purple for Zacatecas
          } else if (booking.room_preference === 'mezzanine') {
            color = '#84cc16'; // Lime for Entire Mezzanine
          }

          // Create start and end times
          const startTime = booking.start_time || '12:00';
          const endTime = booking.end_time || '14:00';

          return {
            id: `booking-${booking.request_id}`,
            title: `🔒 ${booking.event_name}${booking.room_preference ? ` (${booking.room_preference === 'jalisco' ? 'Jalisco' : booking.room_preference === 'zacatecas' ? 'Zacatecas' : 'Entire Mezzanine'})` : ''}`,
            start: new Date(`${booking.event_date}T${startTime}:00`),
            end: new Date(`${booking.event_date}T${endTime}:00`),
            color: color,
            isRoomBooking: true,
            bookingData: booking,
            allDay: false,
          };
        });

      setRoomBookings(privateBookings);
    } catch (err) {
      console.error('Error fetching approved room bookings:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/events?days=365', // Fetch events for the next year
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      // Transform events for react-big-calendar while preserving optional Wix metadata.
      const transformedEvents = (data.events || []).map(transformEventForCalendar);

      setEvents(transformedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleWixSync = async () => {
    if (syncingWix) return;

    try {
      setSyncingWix(true);
      setError(null);
      setWixSyncResult(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const syncOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/events/sync-wix',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });
      const syncResponse = await syncOperation.response;
      const result = await syncResponse.body.json();

      setWixSyncResult({
        fetched: Number(result.fetched) || 0,
        inserted: Number(result.inserted) || 0,
        updated: Number(result.updated) || 0,
        deleted: Number(result.deleted) || 0,
        failed: Number(result.failed) || 0,
      });
      await fetchEvents();
    } catch (err) {
      console.error('Error synchronizing Wix events:', err);
      setError(err.message || 'Failed to synchronize events from Wix');
    } finally {
      setSyncingWix(false);
    }
  };

  // Event Requests Functions
  const fetchEventRequests = async () => {
    try {
      setRequestsLoading(true);
      setError(null);

      const token = await getAuthToken();
      const queryParam = requestStatusFilter !== 'all' ? `?status=${requestStatusFilter}` : '';

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/event-requests${queryParam}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setEventRequests(data.requests || []);
    } catch (err) {
      console.error('Error fetching event requests:', err);
      setError('Failed to fetch event requests');
    } finally {
      setRequestsLoading(false);
    }
  };

  const openRequestModal = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || '');
    setPublicEventTitle(defaultPublicEventTitle(request));
    setShowRequestModal(true);
  };

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setSelectedRequest(null);
    setAdminNotes('');
    setPublicEventTitle('');
    setError(null);
    setSuccessMessage(null);
  };

  const handleUpdateRequestStatus = async (status) => {
    if (!selectedRequest) return;

    const isPublicApproval = selectedRequest.request_type === 'public' && status === 'approved';
    const finalTitle = publicEventTitle.trim();
    if (isPublicApproval && (
      !finalTitle ||
      finalTitle.toLowerCase() === PUBLIC_EVENT_PLACEHOLDER.toLowerCase() ||
      finalTitle.length > 200
    )) {
      setError('Enter a final public event title between 1 and 200 characters.');
      return;
    }

    try {
      setProcessingRequest(true);
      setError(null);

      const token = await getAuthToken();

      const patchOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/event-requests/${selectedRequest.request_id}`,
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            status,
            admin_notes: adminNotes.trim() || undefined,
            ...(isPublicApproval ? { event_name: finalTitle } : {}),
          },
        },
      });

      await patchOperation.response;
      setSuccessMessage(`Request ${status} successfully!`);

      // Refresh the list
      await fetchEventRequests();

      // Refresh room bookings on calendar if status changed
      if (status === 'approved' || status === 'rejected') {
        await fetchApprovedRoomBookings();
      }

      // Close modal after a brief delay
      setTimeout(() => {
        closeRequestModal();
      }, 1500);
    } catch (err) {
      console.error('Error updating request status:', err);
      setError('Failed to update request status');
    } finally {
      setProcessingRequest(false);
    }
  };

  const openCreateModal = (slotInfo) => {
    // Format date for input field
    const selectedDate = moment(slotInfo.start).format('YYYY-MM-DD');

    setModalMode('create');
    setSelectedEvent(null);
    setFormData(getDefaultEventFormData(selectedDate));
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (event) => {
    setModalMode('edit');
    setSelectedEvent(event);
    setFormData({
      ...getDefaultEventFormData(event.event_date),
      title: event.rawTitle || event.title,
      description: event.description || '',
      event_time: event.event_time || '',
      image_url: event.image_url || '',
      color: event.color || '#f97316', // Default to Chulas orange if no color set
      rsvp_enabled: event.source === 'wix' ? false : (event.rsvp_enabled || false),
      rsvp_slots: event.rsvp_slots || 50,
      rsvp_waitlist_enabled: event.rsvp_waitlist_enabled !== undefined ? event.rsvp_waitlist_enabled : true,
      is_featured: event.is_featured || false,
    });
    setImagePreview(event.image_url || null);
    setImageFile(null);
    setShowModal(true);

    // Fetch RSVP stats if RSVP is enabled for this event
    if (event.rsvp_enabled) {
      fetchRsvps(event.id);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMode('create');
    setSelectedEvent(null);
    setFormData(getDefaultEventFormData());
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setSuccessMessage(null);
    // Clear RSVP stats
    setRsvpStats(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'recurring_enabled') {
      const startDay = getWeekdayTokenForDate(formData.event_date);
      setFormData(prev => ({
        ...prev,
        recurring_enabled: checked,
        recurrence_days: checked && prev.recurrence_days.length === 0 && startDay
          ? [startDay]
          : prev.recurrence_days,
        rsvp_enabled: checked ? false : prev.rsvp_enabled,
        is_featured: checked ? false : prev.is_featured,
      }));
      if (checked) {
        setImageFile(null);
        setImagePreview(null);
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleRecurrenceDayToggle = (token) => {
    setFormData(prev => {
      const selected = new Set(prev.recurrence_days || []);
      if (selected.has(token)) {
        selected.delete(token);
      } else {
        selected.add(token);
      }
      return {
        ...prev,
        recurrence_days: RECURRENCE_WEEKDAYS
          .map(day => day.token)
          .filter(dayToken => selected.has(dayToken)),
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (eventId) => {
    if (!imageFile) return null;

    try {
      setUploadingImage(true);

      // Convert image to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      const base64Data = await base64Promise;

      // Upload to backend
      const token = await getAuthToken();
      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${eventId}/upload-image`,
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            image_data: base64Data,
            content_type: imageFile.type,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      return data.image_url;
    } catch (err) {
      console.error('Error uploading image:', err);
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.event_date) {
      setError('Title and date are required');
      return;
    }

    const recurrencePreview = getRecurrencePreview(formData);
    if (modalMode === 'create' && formData.recurring_enabled && recurrencePreview.error) {
      setError(recurrencePreview.error);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      if (modalMode === 'create') {
        // Create event
        const createBody = {
          title: formData.title.trim(),
          description: formData.description.trim(),
          event_date: formData.event_date,
          event_time: formData.event_time || null,
          color: formData.color,
          rsvp_enabled: formData.recurring_enabled ? false : formData.rsvp_enabled,
          rsvp_slots: !formData.recurring_enabled && formData.rsvp_enabled ? formData.rsvp_slots : undefined,
          rsvp_waitlist_enabled: !formData.recurring_enabled && formData.rsvp_enabled ? formData.rsvp_waitlist_enabled : undefined,
          is_featured: formData.recurring_enabled ? false : formData.is_featured,
        };

        if (formData.recurring_enabled) {
          createBody.recurrence = {
            type: 'weekly',
            weekdays: formData.recurrence_days,
            end_date: recurrencePreview.effectiveEndDate,
          };
        }

        const createOperation = post({
          apiName: 'ChulasAdminAPI',
          path: '/v1/admin/events',
          options: {
            headers: {
              Authorization: token,
            },
            body: createBody,
          },
        });

        const createResponse = await createOperation.response;
        const createData = await createResponse.body.json();

        // Upload image if selected. Images are intentionally disabled for recurring events.
        if (!formData.recurring_enabled && imageFile) {
          await uploadImage(createData.event.event_id);
        }

        setSuccessMessage(
          formData.recurring_enabled
            ? `Created ${createData.created_count || recurrencePreview.count} events successfully!`
            : 'Event created successfully!'
        );
      } else if (modalMode === 'edit') {
        // Upload image first if selected
        if (imageFile) {
          const imageUrl = await uploadImage(selectedEvent.id);
          formData.image_url = imageUrl;
        }

        // Update event
        const updateOperation = patch({
          apiName: 'ChulasAdminAPI',
          path: `/v1/admin/events/${selectedEvent.id}`,
          options: {
            headers: {
              Authorization: token,
            },
            body: {
              title: formData.title.trim(),
              description: formData.description.trim(),
              event_date: formData.event_date,
              event_time: formData.event_time || null,
              color: formData.color,
              ...(formData.image_url && { image_url: formData.image_url }),
              rsvp_enabled: selectedEvent.source === 'wix' ? false : formData.rsvp_enabled,
              rsvp_slots: selectedEvent.source !== 'wix' && formData.rsvp_enabled ? formData.rsvp_slots : undefined,
              rsvp_waitlist_enabled: selectedEvent.source !== 'wix' && formData.rsvp_enabled ? formData.rsvp_waitlist_enabled : undefined,
              is_featured: formData.is_featured,
            },
          },
        });

        await updateOperation.response;
        setSuccessMessage('Event updated successfully!');

        // Refresh RSVP stats if RSVP is enabled
        if (formData.rsvp_enabled) {
          await fetchRsvps(selectedEvent.id);
        }
      }

      // Refresh events list
      await fetchEvents();

      // Close modal after a brief delay
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err.message || 'Failed to save event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    let deleteScope = 'occurrence';
    let successText = 'Event deleted successfully!';

    if (selectedEvent?.is_recurring_occurrence && selectedEvent.recurrence_group_id) {
      const seriesCount = events.filter(event => event.recurrence_group_id === selectedEvent.recurrence_group_id).length;
      const choice = window.prompt(
        `This event is part of a recurring series.\n\n` +
        `Type "one" to delete only this occurrence.\n` +
        `Type "${seriesCount}" to delete the whole series (all occurrences, including any past dates).\n\n` +
        `Cancel to keep the event.`
      );

      if (choice === null) {
        return;
      }

      const normalizedChoice = choice.trim().toLowerCase();
      if (normalizedChoice === 'one') {
        deleteScope = 'occurrence';
        successText = 'Recurring occurrence deleted successfully!';
      } else if (normalizedChoice === String(seriesCount)) {
        deleteScope = 'series';
        // The backend deletes the entire series (including past occurrences
        // that fall outside the loaded window), so the authoritative count
        // comes from the delete response below.
        successText = 'Recurring series deleted successfully!';
      } else {
        setError(`Delete cancelled. Type "one" or "${seriesCount}" to confirm.`);
        return;
      }
    } else if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const deleteOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${selectedEvent.id}?scope=${deleteScope}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const deleteResponse = await deleteOperation.response;
      if (deleteScope === 'series') {
        try {
          const deleteData = await deleteResponse.body.json();
          if (deleteData && typeof deleteData.deleted_count === 'number') {
            successText = `Deleted all ${deleteData.deleted_count} occurrences in this series successfully!`;
          }
        } catch (parseErr) {
          // Fall back to the generic series message if the body can't be read.
          console.warn('Could not read delete response body:', parseErr);
        }
      }
      setSuccessMessage(successText);

      // Refresh events list
      await fetchEvents();

      // Close modal
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message || 'Failed to delete event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // RSVP Management Functions
  const fetchRsvps = async (eventId) => {
    try {
      setLoadingRsvps(true);
      const token = await getAuthToken();

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${eventId}/rsvps`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setRsvpList(data.rsvps || []);
      setRsvpStats(data.status_counts || {});
    } catch (err) {
      console.error('Error fetching RSVPs:', err);
      setError('Failed to fetch RSVPs');
    } finally {
      setLoadingRsvps(false);
    }
  };

  const openRsvpModal = (event) => {
    if (!event.rsvp_enabled) {
      alert('RSVPs are not enabled for this event');
      return;
    }
    setSelectedEvent(event);
    setShowRsvpModal(true);
    fetchRsvps(event.id);
  };

  const closeRsvpModal = () => {
    setShowRsvpModal(false);
    setSelectedEvent(null);
    setRsvpList([]);
    setRsvpStats(null);
  };

  const handleCancelRsvp = async (userId) => {
    if (!window.confirm('Are you sure you want to cancel this user\'s RSVP?')) {
      return;
    }

    try {
      const token = await getAuthToken();

      const deleteOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${selectedEvent.id}/rsvps/${userId}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await deleteOperation.response;

      // Refresh RSVP list
      fetchRsvps(selectedEvent.id);
      setSuccessMessage('RSVP cancelled successfully');

      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error cancelling RSVP:', err);
      setError('Failed to cancel RSVP');
    }
  };

  // Helper to get events and bookings for a specific date
  const getScheduleForDate = useCallback((dateString) => {
    const targetDate = moment(dateString).format('YYYY-MM-DD');

    // Get public events for this date
    const eventsOnDate = events.filter(event =>
      moment(event.start).format('YYYY-MM-DD') === targetDate
    );

    // Get room bookings for this date (from approved requests)
    const bookingsOnDate = roomBookings.filter(booking =>
      moment(booking.start).format('YYYY-MM-DD') === targetDate
    );

    return { eventsOnDate, bookingsOnDate };
  }, [events, roomBookings]);

  // Check for room conflicts
  const checkRoomConflicts = useCallback((requestDate, roomPreference, startTime, endTime) => {
    const { bookingsOnDate } = getScheduleForDate(requestDate);

    if (!roomPreference || bookingsOnDate.length === 0) {
      return { hasConflict: false, conflicts: [] };
    }

    const conflicts = bookingsOnDate.filter(booking => {
      const bookingRoom = booking.bookingData?.room_preference;

      // Mezzanine conflicts with everything (jalisco, zacatecas, or mezzanine)
      if (roomPreference === 'mezzanine') {
        return true; // Any room booking is a conflict
      }

      // Individual room conflicts with same room or mezzanine
      if (bookingRoom === roomPreference || bookingRoom === 'mezzanine') {
        return true;
      }

      return false;
    });

    return { hasConflict: conflicts.length > 0, conflicts };
  }, [getScheduleForDate]);

  // Custom event style
  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.color || '#f97316', // Use event's custom color or default to Chulas orange
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    const classNames = [
      event.is_recurring_occurrence ? 'recurring-calendar-event' : '',
      event.source === 'wix' ? 'wix-calendar-event' : '',
    ].filter(Boolean);
    return {
      style,
      className: classNames.join(' '),
    };
  };

  const recurrencePreview = getRecurrencePreview(formData);
  const recurrenceDefaultEndDate = getDefaultRecurrenceEndDate(formData.event_date);
  const recurrenceStartDate = parseDateInput(formData.event_date);
  const recurrenceMaxEndDate = recurrenceStartDate
    ? formatDateInput(addMonthsToDate(recurrenceStartDate, MAX_RECURRING_MONTHS))
    : '';

  return (
    <div className="event-calendar-container">
      <h1>Event Calendar</h1>
      <p className="page-description">
        Create and manage events for the "What's Happening" section of the mobile app.
      </p>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar
        </button>
        <button
          className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Event Requests
          {eventRequests.filter(r => r.status === 'pending').length > 0 && activeTab !== 'requests' && (
            <span className="badge">{eventRequests.filter(r => r.status === 'pending').length}</span>
          )}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="calendar-wrapper">
          <div className="calendar-actions">
            <div className="calendar-action-copy">
              <strong>Wix Events</strong>
              <span>Insert upcoming events that have not already been imported.</span>
            </div>
            <button
              type="button"
              className="btn btn-wix-sync"
              onClick={handleWixSync}
              disabled={syncingWix || loading}
              aria-busy={syncingWix}
            >
              {syncingWix ? 'Syncing from Wix…' : 'Sync from Wix'}
            </button>
          </div>

          {wixSyncResult && (
            <div
              className={`alert ${wixSyncResult.failed > 0 ? 'alert-warning' : 'alert-success'} wix-sync-result`}
              role="status"
            >
              Wix mirror complete: fetched {wixSyncResult.fetched}, imported {wixSyncResult.inserted}, refreshed {wixSyncResult.updated}, removed {wixSyncResult.deleted}, failed {wixSyncResult.failed}.
            </div>
          )}

          {/* Legend for calendar */}
          <div className="calendar-legend">
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#f97316'}}></span> Events</span>
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#7c3aed'}}></span> Jalisco Room</span>
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#a855f7'}}></span> Zacatecas Room</span>
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#84cc16'}}></span> Entire Mezzanine</span>
            <span className="legend-item"><span className="wix-legend-icon">Ⓦ</span> Wix import</span>
            <span className="legend-item"><span className="wix-external-legend-icon">↗</span> Customer opens Wix</span>
            <span className="legend-item"><span className="recurring-legend-icon">🔁</span> Recurring event</span>
          </div>
          <Calendar
          localizer={localizer}
          events={[...events, ...roomBookings]}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 700 }}
          onSelectSlot={openCreateModal}
          onSelectEvent={(event) => {
            if (event.isRoomBooking) {
              // Open the request modal for room bookings
              openRequestModal(event.bookingData);
            } else {
              openEditModal(event);
            }
          }}
          selectable
          eventPropGetter={eventStyleGetter}
          views={['month', 'week', 'day']}
          view={currentView}
          onView={setCurrentView}
          date={currentDate}
          onNavigate={setCurrentDate}
        />
        </div>
      )}

      {/* Event Requests Tab */}
      {activeTab === 'requests' && (
        <div className="requests-wrapper">
          {/* Status Filter */}
          <div className="filter-bar">
            <label>Filter by Status:</label>
            <select
              value={requestStatusFilter}
              onChange={(e) => setRequestStatusFilter(e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
          </div>

          {requestsLoading ? (
            <div className="loading">Loading event requests...</div>
          ) : eventRequests.length === 0 ? (
            <div className="empty-state">
              <p>No {requestStatusFilter !== 'all' ? requestStatusFilter : ''} event requests found.</p>
            </div>
          ) : (
            <div className="requests-list">
              {eventRequests.map((request) => (
                <div
                  key={request.request_id}
                  className={`request-card request-${request.status}`}
                  onClick={() => openRequestModal(request)}
                >
                  <div className="request-card-header">
                    <span className={`request-type-badge ${request.request_type}`}>
                      {request.request_type === 'private' ? 'Private Room' : 'Perform at Chula\'s'}
                    </span>
                    <span className={`status-badge status-${request.status}`}>
                      {request.status}
                    </span>
                  </div>
                  <h3>{requestDisplayTitle(request)}</h3>
                  <div className="request-card-meta">
                    <span>
                      <strong>Date:</strong> {moment(request.event_date).format('MMM D, YYYY')}
                      {request.request_type === 'private' && request.start_time && request.end_time
                        ? ` (${formatClockTime(request.start_time)} – ${formatClockTime(request.end_time)})`
                        : request.event_time && ` at ${formatClockTime(request.event_time)}`}
                    </span>
                    {request.request_type === 'private' && request.guest_count && (
                      <span><strong>Guests:</strong> {request.guest_count}</span>
                    )}
                    {request.room_preference && (
                      <span><strong>Room:</strong> {request.room_preference === 'jalisco' ? 'Jalisco Room' : request.room_preference === 'zacatecas' ? 'Zacatecas Room' : 'Entire Mezzanine'}</span>
                    )}
                  </div>
                  <p className="request-description">{request.description}</p>
                  <div className="request-card-footer">
                    <span><strong>Contact:</strong> {request.contact_email}</span>
                    <span className="request-date">Submitted {moment(request.created_at).format('MMM D, YYYY h:mm A')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Create New Event' : 'Edit Event'}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>

            {selectedEvent?.source === 'wix' && (
              <div className="wix-source-notice">
                <strong>Imported from Wix</strong>
                <span>
                  Changes made here are replaced by the next Wix sync. Customer registration stays on Wix
                  {selectedEvent.wix_registration_type ? ` (${selectedEvent.wix_registration_type})` : ''}.
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="event-form">
              <div className="form-group">
                <label htmlFor="title">Event Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter event title..."
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter event description..."
                  rows="4"
                  disabled={loading}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="event_date">Date *</label>
                  <input
                    type="date"
                    id="event_date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="event_time">Time</label>
                  <input
                    type="time"
                    id="event_time"
                    name="event_time"
                    value={formData.event_time}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>

              {modalMode === 'create' && (
                <div className="form-group recurring-toggle-section">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="recurring_enabled"
                      name="recurring_enabled"
                      checked={formData.recurring_enabled}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                    <label htmlFor="recurring_enabled">Recurring event</label>
                  </div>
                  <small>Create one event occurrence for each matching weekday in the selected date range.</small>
                </div>
              )}

              {modalMode === 'create' && formData.recurring_enabled && (
                <div className="recurring-controls">
                  <div className="form-group">
                    <label>Repeat weekly on *</label>
                    <div className="weekday-selector">
                      {RECURRENCE_WEEKDAYS.map(day => (
                        <label key={day.token} className="weekday-option">
                          <input
                            type="checkbox"
                            checked={(formData.recurrence_days || []).includes(day.token)}
                            onChange={() => handleRecurrenceDayToggle(day.token)}
                            disabled={loading}
                          />
                          <span>{day.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="recurrence_end_date">Repeat until</label>
                    <input
                      type="date"
                      id="recurrence_end_date"
                      name="recurrence_end_date"
                      value={formData.recurrence_end_date}
                      onChange={handleInputChange}
                      min={formData.event_date}
                      max={recurrenceMaxEndDate}
                      disabled={loading}
                    />
                    <small>
                      Optional. Leave blank to repeat through {recurrenceDefaultEndDate || 'six months from the start date'}.
                      Maximum range is {MAX_RECURRING_MONTHS} months.
                    </small>
                  </div>

                  <div className={`recurrence-preview ${recurrencePreview.error ? 'recurrence-preview-error' : ''}`}>
                    {recurrencePreview.error
                      ? recurrencePreview.error
                      : `This will create ${recurrencePreview.count} event${recurrencePreview.count === 1 ? '' : 's'}.`}
                  </div>

                  <div className="recurring-limit-note">
                    Recurring events cannot be featured, use images, or accept RSVPs in this MVP.
                  </div>
                </div>
              )}

              {!formData.recurring_enabled && !selectedEvent?.is_recurring_occurrence && (
                <div className="form-group">
                  <label htmlFor="image">Event Image</label>
                  <input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={loading}
                  />
                  <small>Max size: 5MB. Supported formats: JPG, PNG, GIF</small>

                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Event preview" />
                    </div>
                  )}
                </div>
              )}

              {/* Color Picker */}
              <div className="form-group">
                <label htmlFor="color">Event Color</label>
                <div className="color-picker-wrapper">
                  <div className="color-presets">
                    {[
                      { name: 'Lime', color: '#D6DE23' },
                      { name: 'Orange', color: '#F8A054' },
                      { name: 'Red', color: '#F05960' },
                      { name: 'Purple', color: '#7060A9' },
                      { name: 'Teal', color: '#00B29C' },
                    ].map((preset) => (
                      <button
                        key={preset.color}
                        type="button"
                        className={`color-preset-btn ${formData.color.toUpperCase() === preset.color.toUpperCase() ? 'selected' : ''}`}
                        style={{ backgroundColor: preset.color }}
                        onClick={() => setFormData(prev => ({ ...prev, color: preset.color }))}
                        disabled={loading}
                        title={preset.name}
                      >
                        {formData.color.toUpperCase() === preset.color.toUpperCase() && (
                          <span className="color-check">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="color-custom">
                    <input
                      type="color"
                      id="color"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="color-input-native"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={handleInputChange}
                      name="color"
                      placeholder="#F8A054"
                      disabled={loading}
                      className="color-input-text"
                    />
                  </div>
                </div>
                <small>Select a Chula's brand color or choose a custom color</small>
              </div>

              {!formData.recurring_enabled && !selectedEvent?.is_recurring_occurrence && (
                <>
                  {/* Featured Event Toggle */}
                  <div className="form-group">
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="is_featured"
                        name="is_featured"
                        checked={formData.is_featured}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <label htmlFor="is_featured">Feature on Home Screen</label>
                    </div>
                    <small>Featured events appear in "What's Happening" on the mobile app home screen</small>
                  </div>

                  {selectedEvent?.source !== 'wix' && (
                    <>
                      {/* RSVP Settings */}
                      <div className="form-group">
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="rsvp_enabled"
                            name="rsvp_enabled"
                            checked={formData.rsvp_enabled}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <label htmlFor="rsvp_enabled">Enable RSVPs for this event</label>
                        </div>
                      </div>

                      {formData.rsvp_enabled && (
                        <>
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor="rsvp_slots">Number of Slots *</label>
                              <input
                                type="number"
                                id="rsvp_slots"
                                name="rsvp_slots"
                                value={formData.rsvp_slots}
                                onChange={handleInputChange}
                                min="1"
                                required={formData.rsvp_enabled}
                                disabled={loading}
                              />
                            </div>

                            <div className="form-group">
                              <div className="checkbox-group">
                                <input
                                  type="checkbox"
                                  id="rsvp_waitlist_enabled"
                                  name="rsvp_waitlist_enabled"
                                  checked={formData.rsvp_waitlist_enabled}
                                  onChange={handleInputChange}
                                  disabled={loading}
                                />
                                <label htmlFor="rsvp_waitlist_enabled">Enable Waitlist</label>
                              </div>
                            </div>
                          </div>

                          {modalMode === 'edit' && (
                            <div className="form-group">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => openRsvpModal(selectedEvent)}
                                disabled={loading}
                              >
                                Manage RSVPs ({rsvpStats?.confirmed || 0} confirmed, {rsvpStats?.waitlist || 0} waitlist)
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success">
                  {successMessage}
                </div>
              )}

              <div className="modal-actions">
                {modalMode === 'edit' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    Delete Event
                  </button>
                )}
                <div className="modal-actions-right">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || uploadingImage || (modalMode === 'create' && formData.recurring_enabled && !!recurrencePreview.error)}
                  >
                    {loading || uploadingImage
                      ? uploadingImage ? 'Uploading Image...' : 'Saving...'
                      : modalMode === 'create'
                        ? formData.recurring_enabled ? 'Create Events' : 'Create Event'
                        : 'Update Event'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RSVP Management Modal */}
      {showRsvpModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeRsvpModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage RSVPs - {selectedEvent.title}</h2>
              <button className="modal-close" onClick={closeRsvpModal}>&times;</button>
            </div>

            {successMessage && (
              <div className="alert alert-success">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            {loadingRsvps ? (
              <div className="loading">Loading RSVPs...</div>
            ) : (
              <>
                {/* RSVP Stats */}
                {rsvpStats && (
                  <div className="rsvp-stats">
                    <div className="stat-card">
                      <h4>Total Slots</h4>
                      <p className="stat-number">{selectedEvent.rsvp_slots}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Confirmed</h4>
                      <p className="stat-number confirmed">{rsvpStats.confirmed || 0}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Waitlist</h4>
                      <p className="stat-number waitlist">{rsvpStats.waitlist || 0}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Cancelled</h4>
                      <p className="stat-number cancelled">{rsvpStats.cancelled || 0}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Available</h4>
                      <p className="stat-number available">
                        {selectedEvent.rsvp_slots - (rsvpStats.confirmed || 0)}
                      </p>
                    </div>
                  </div>
                )}

                {/* RSVP List */}
                {rsvpList.length === 0 ? (
                  <div className="empty-state">
                    <p>No RSVPs yet for this event.</p>
                  </div>
                ) : (
                  <div className="rsvp-list">
                    <table className="rsvp-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Notes</th>
                          <th>RSVP Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rsvpList.map((rsvp) => (
                          <tr key={rsvp.user_id} className={`rsvp-row rsvp-${rsvp.status}`}>
                            <td>
                              <span className={`status-badge status-${rsvp.status}`}>
                                {rsvp.status}
                              </span>
                            </td>
                            <td>{rsvp.user_email || 'N/A'}</td>
                            <td>{rsvp.user_phone || 'N/A'}</td>
                            <td className="notes-cell">{rsvp.notes || '-'}</td>
                            <td>{new Date(rsvp.created_at).toLocaleString()}</td>
                            <td>
                              {rsvp.status !== 'cancelled' && (
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleCancelRsvp(rsvp.user_id)}
                                >
                                  Cancel
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRsvpModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Request Detail Modal */}
      {showRequestModal && selectedRequest && (
        <div className="modal-overlay" onClick={closeRequestModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Event Request Details</h2>
              <button className="modal-close" onClick={closeRequestModal}>&times;</button>
            </div>

            <div className="request-detail-content">
              {/* Status Banner */}
              <div className={`status-banner status-${selectedRequest.status}`}>
                <span className={`request-type-badge ${selectedRequest.request_type}`}>
                  {selectedRequest.request_type === 'private' ? 'Private Room' : 'Perform at Chula\'s'}
                </span>
                <span className={`status-badge status-${selectedRequest.status}`}>
                  {selectedRequest.status.toUpperCase()}
                </span>
              </div>

              {/* Event Details */}
              <div className="request-detail-section">
                <h3>{requestDisplayTitle(selectedRequest)}</h3>

                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Requested Date</label>
                    <span>{moment(selectedRequest.event_date).format('dddd, MMMM D, YYYY')}</span>
                  </div>
                  {selectedRequest.request_type === 'private' && selectedRequest.start_time && selectedRequest.end_time ? (
                    <div className="detail-item">
                      <label>Requested Time</label>
                      <span>{formatClockTime(selectedRequest.start_time)} – {formatClockTime(selectedRequest.end_time)}</span>
                    </div>
                  ) : selectedRequest.event_time && (
                    <div className="detail-item">
                      <label>Requested Time</label>
                      <span>{formatClockTime(selectedRequest.event_time)}</span>
                    </div>
                  )}
                  {selectedRequest.request_type === 'private' && (
                    <>
                      <div className="detail-item">
                        <label>Number of Guests</label>
                        <span>{selectedRequest.guest_count || 'Not specified'}</span>
                      </div>
                      {selectedRequest.room_preference && (
                        <div className="detail-item">
                          <label>Room Preference</label>
                          <span>
                            {selectedRequest.room_preference === 'jalisco'
                              ? 'The Jalisco Room (Capacity: 60)'
                              : selectedRequest.room_preference === 'zacatecas'
                              ? 'The Zacatecas Room (Capacity: 34)'
                              : 'Entire Mezzanine (Capacity: 94)'}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="detail-item full-width">
                  <label>Description</label>
                  <p className="description-text">{selectedRequest.description}</p>
                </div>
              </div>

              {/* Schedule for Requested Date - Conflict Check */}
              {(() => {
                const { eventsOnDate, bookingsOnDate } = getScheduleForDate(selectedRequest.event_date);
                const { hasConflict, conflicts } = selectedRequest.request_type === 'private'
                  ? checkRoomConflicts(selectedRequest.event_date, selectedRequest.room_preference)
                  : { hasConflict: false, conflicts: [] };

                return (
                  <div className={`request-detail-section schedule-section ${hasConflict ? 'has-conflict' : ''}`}>
                    <h4>
                      Schedule for {moment(selectedRequest.event_date).format('MMMM D, YYYY')}
                      {hasConflict && <span className="conflict-warning"> - Conflict Detected!</span>}
                    </h4>

                    {eventsOnDate.length === 0 && bookingsOnDate.length === 0 ? (
                      <p className="no-schedule">No events or room bookings scheduled for this date.</p>
                    ) : (
                      <div className="schedule-list">
                        {/* Public Events */}
                        {eventsOnDate.length > 0 && (
                          <div className="schedule-group">
                            <label>Public Events</label>
                            {eventsOnDate.map(event => (
                              <div key={event.id} className="schedule-item event-item">
                                <span className="schedule-color" style={{ backgroundColor: event.color || '#f97316' }}></span>
                                <div className="schedule-info">
                                  <strong>{event.title}</strong>
                                  {event.event_time && <span className="schedule-time">{formatClockTime(event.event_time)}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Room Bookings */}
                        {bookingsOnDate.length > 0 && (
                          <div className="schedule-group">
                            <label>Room Bookings</label>
                            {bookingsOnDate.map(booking => {
                              const isConflicting = conflicts.some(c => c.id === booking.id);
                              const roomName = booking.bookingData?.room_preference === 'jalisco'
                                ? 'Jalisco Room'
                                : booking.bookingData?.room_preference === 'zacatecas'
                                ? 'Zacatecas Room'
                                : 'Entire Mezzanine';

                              return (
                                <div key={booking.id} className={`schedule-item booking-item ${isConflicting ? 'conflicting' : ''}`}>
                                  <span className="schedule-color" style={{ backgroundColor: booking.color }}></span>
                                  <div className="schedule-info">
                                    <strong>{booking.bookingData?.event_name}</strong>
                                    <span className="room-badge">{roomName}</span>
                                    {booking.bookingData?.start_time && booking.bookingData?.end_time && (
                                      <span className="schedule-time">
                                        {formatClockTime(booking.bookingData.start_time)} – {formatClockTime(booking.bookingData.end_time)}
                                      </span>
                                    )}
                                    {isConflicting && <span className="conflict-badge">CONFLICT</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Contact Information */}
              <div className="request-detail-section">
                <h4>Contact Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Email</label>
                    <span>{selectedRequest.contact_email}</span>
                  </div>
                  {selectedRequest.contact_phone && (
                    <div className="detail-item">
                      <label>Phone</label>
                      <span>{selectedRequest.contact_phone}</span>
                    </div>
                  )}
                </div>
                <div className="detail-item">
                  <label>Submitted</label>
                  <span>{moment(selectedRequest.created_at).format('MMMM D, YYYY [at] h:mm A')}</span>
                </div>
              </div>

              {/* Public performer requests get their customer-facing title here. */}
              {selectedRequest.request_type === 'public' && selectedRequest.status === 'pending' && (
                <div className="request-detail-section">
                  <h4>Public Event Title *</h4>
                  <label className="request-title-label" htmlFor="public-event-title">
                    Confirm or edit the title that will appear in the customer event calendar.
                  </label>
                  <input
                    id="public-event-title"
                    type="text"
                    value={publicEventTitle}
                    onChange={(e) => setPublicEventTitle(e.target.value)}
                    maxLength="200"
                    required
                    disabled={processingRequest}
                  />
                  <span className="request-title-count">{publicEventTitle.length}/200</span>
                </div>
              )}

              {/* Admin Notes */}
              <div className="request-detail-section">
                <h4>Admin Notes</h4>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes for the requester (will be visible to them)..."
                  rows="3"
                  disabled={selectedRequest.status !== 'pending' || processingRequest}
                />
              </div>

              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success">
                  {successMessage}
                </div>
              )}

              {/* Actions */}
              {(() => {
                const { hasConflict, conflicts } = selectedRequest.request_type === 'private'
                  ? checkRoomConflicts(selectedRequest.event_date, selectedRequest.room_preference)
                  : { hasConflict: false, conflicts: [] };

                const handleApproveWithConflictCheck = () => {
                  if (hasConflict) {
                    const conflictDetails = conflicts.map(c => {
                      const room = c.bookingData?.room_preference === 'jalisco'
                        ? 'Jalisco Room'
                        : c.bookingData?.room_preference === 'zacatecas'
                        ? 'Zacatecas Room'
                        : 'Entire Mezzanine';
                      return `- ${c.bookingData?.event_name} (${room})`;
                    }).join('\n');

                    const confirmed = window.confirm(
                      `⚠️ CONFLICT DETECTED!\n\nThis request conflicts with existing bookings:\n${conflictDetails}\n\nAre you sure you want to approve this request anyway?`
                    );
                    if (!confirmed) return;
                  }
                  handleUpdateRequestStatus('approved');
                };

                return (
                  <div className="modal-actions request-actions">
                    {selectedRequest.status === 'pending' ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => handleUpdateRequestStatus('rejected')}
                          disabled={processingRequest}
                        >
                          {processingRequest ? 'Processing...' : 'Reject Request'}
                        </button>
                        <div className="modal-actions-right">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={closeRequestModal}
                            disabled={processingRequest}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className={`btn ${hasConflict ? 'btn-warning' : 'btn-success'}`}
                            onClick={handleApproveWithConflictCheck}
                            disabled={processingRequest || (
                              selectedRequest.request_type === 'public' &&
                              (
                                !publicEventTitle.trim() ||
                                publicEventTitle.trim().toLowerCase() === PUBLIC_EVENT_PLACEHOLDER.toLowerCase()
                              )
                            )}
                          >
                            {processingRequest ? 'Processing...' : hasConflict ? '⚠️ Approve (Conflict)' : 'Approve Request'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeRequestModal}
                      >
                        Close
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export {
  addMonthsToDate,
  buildLocalEventDate,
  formatClockTime,
  formatDateInput,
  getDefaultEventFormData,
  getDefaultRecurrenceEndDate,
  getRecurrencePreview,
  parseDateInput,
  transformEventForCalendar,
  defaultPublicEventTitle,
  requestDisplayTitle,
};

export default EventCalendar;
